import { ShieldCheck, PhoneCall, FileCheck2 } from "lucide-react";
import Reveal from "../components/Reveal";

const ABOUT_IMAGE =
    "https://images.pexels.com/photos/7109013/pexels-photo-7109013.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const VALUES = [
    {
        icon: ShieldCheck,
        title: "Accurate, honest information",
        body: "We describe our services plainly and confirm what is supported in your market before anything is agreed.",
    },
    {
        icon: PhoneCall,
        title: "Phone-first service",
        body: "You reach a real team by phone or email — no ticket queues, no forms disappearing into a system.",
    },
    {
        icon: FileCheck2,
        title: "A transparent process",
        body: "From the first call to ongoing administration, you always know the next step and who is responsible for it.",
    },
];

export default function About() {
    return (
        <section
            id="about"
            data-testid="about-section"
            className="dot-grid-light bg-mist py-24 lg:py-32"
        >
            <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
                <Reveal className="lg:col-span-5">
                    <div
                        className="relative overflow-hidden rounded-2xl border border-navy/10 shadow-sm"
                        style={{
                            clipPath:
                                "polygon(0 0, 100% 0, 100% calc(100% - 48px), calc(100% - 48px) 100%, 0 100%)",
                        }}
                    >
                        <img
                            src={ABOUT_IMAGE}
                            alt="Professionals discussing international employment plans in a modern office"
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                        />
                    </div>
                    <div className="mt-4 flex items-center gap-3 pl-1">
                        <span className="h-px w-10 bg-gold" aria-hidden="true" />
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-navy/50">
                            A registered business serving supported markets
                        </p>
                    </div>
                </Reveal>

                <div className="lg:col-span-7">
                    <Reveal>
                        <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.28em] text-royal">
                            <span className="h-px w-10 bg-gold" aria-hidden="true" />
                            Who We Are
                        </p>
                        <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                            About Hexagon
                        </h2>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-navy/65 sm:text-lg">
                            Hexagon EOR is a professional services firm focused
                            on employer of record, international employment
                            administration, and related legal and tax services.
                            We help businesses — primarily in the United States
                            — evaluate and coordinate practical employment
                            solutions for engaging talent in supported markets,
                            with additional service availability in Canada.
                        </p>
                        <p className="mt-4 max-w-2xl text-base leading-relaxed text-navy/65">
                            We keep our claims as careful as our work: we only
                            discuss services we can genuinely deliver, and we
                            confirm market coverage for your situation before
                            you commit to anything.
                        </p>
                    </Reveal>

                    <div className="mt-10 grid gap-5 sm:grid-cols-3">
                        {VALUES.map((value, i) => (
                            <Reveal key={value.title} delay={0.08 * i}>
                                <div
                                    data-testid={`about-value-${i}`}
                                    className="h-full rounded-2xl border border-navy/10 bg-white p-6"
                                >
                                    <value.icon
                                        size={20}
                                        className="text-royal"
                                        strokeWidth={1.8}
                                        aria-hidden="true"
                                    />
                                    <h3 className="mt-4 font-display text-sm font-bold tracking-tight text-navy">
                                        {value.title}
                                    </h3>
                                    <p className="mt-2 text-[13px] leading-relaxed text-navy/60">
                                        {value.body}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
